import {useUpmPlant} from '@hconnect/upm'
import {Button, Loading, spawnErrorToast} from '@hm/fundamental'
import {mdiPlus} from '@mdi/js'
import {ChangeEvent, useRef} from 'react'
import {useTranslation} from 'react-i18next'

import {ShutdownDetailCollapsible} from './ShutdownDetailCollapsible'

import {AttachedDocumentFileView} from '@refra/modules/common/components/documents'
import {useAttachedDocumentsFilesQuery} from '@refra/modules/common/hooks/queries'
import {useKilns} from '@refra/modules/common/providers'
import {checkFileFormat, checkFileSize} from '@refra/modules/common/utils'
import {
  useAddStaticPartDocuments,
  useDeleteStaticPartDocument
} from '@refra/modules/refra-static-parts/hooks/mutations'
import {useStaticPartDocumentsQuery} from '@refra/modules/refra-static-parts/hooks/queries/useStaticPartDocumentsQuery'
import {useUrlParam} from '@refra/modules/routing/hooks'

interface ShutdownDetailDocumentsProps {
  staticPartId: number
  shutdownId: number
  isReadOnly?: boolean
}

export const ShutdownDetailDocuments: React.FC<ShutdownDetailDocumentsProps> = ({
  staticPartId,
  shutdownId,
  isReadOnly = false
}) => {
  const {t} = useTranslation()

  const plantId = useUrlParam('plantCode')
  const upmPlant = useUpmPlant({plantId})
  const {selectedKiln} = useKilns()

  const {data: documents, isFetching: isLoadingDocuments} = useStaticPartDocumentsQuery({
    plantUpmId: upmPlant.upmId,
    kilnUpmId: selectedKiln.upmId,
    staticPartId,
    shutdownId
  })

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const staticPartReplacementDocuments = documents ?? []

  const documentsSectionTitle = `${t('common.attachments')} (${
    staticPartReplacementDocuments.length
  })`

  const {data: attachedDocumentsFiles} = useAttachedDocumentsFilesQuery({
    documents: staticPartReplacementDocuments
  })

  const {mutate: addDocuments, isPending: isAddingDocuments} = useAddStaticPartDocuments()
  const {mutate: deleteDocument, isPending: isDeletingDocument} = useDeleteStaticPartDocument()

  const onAddDocumentClick = (e) => {
    e.preventDefault()
    fileInputRef?.current?.click()
  }

  const onDeleteDocumentClick = (documentId: number) => {
    return deleteDocument({
      plantUpmId: upmPlant.upmId,
      kilnUpmId: selectedKiln.upmId,
      shutdownId,
      staticPartId,
      documentId: documentId
    })
  }

  const onFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    if (!checkFileSize(selectedFile)) {
      return spawnErrorToast(t('error.fileTooLarge'))
    }

    if (!checkFileFormat(selectedFile)) {
      return spawnErrorToast(t('error.fileFormat'))
    }

    return addDocuments({
      plantUpmId: upmPlant.upmId,
      kilnUpmId: selectedKiln.upmId,
      shutdownId,
      staticPartId,
      documents: [selectedFile]
    })
  }

  if (!attachedDocumentsFiles) {
    return (
      <div className="flex justify-center">
        <Loading size="large" />
      </div>
    )
  }

  const areDocumentsDisabled = isReadOnly || isAddingDocuments || isDeletingDocument

  if (isLoadingDocuments) {
    return <Loading size="large" data-cy="static_part_shutdown_detail_documents_loader" />
  }

  return (
    <ShutdownDetailCollapsible
      title={documentsSectionTitle}
      defaultOpen={true}
      rightSlot={
        <div className="flex justify-between items-center">
          <Button
            icon={mdiPlus}
            variant="tertiary"
            round
            square
            size="small"
            disabled={areDocumentsDisabled}
            onClick={onAddDocumentClick}
            data-cy="static_part_replacemet_add_document_button"
          />
          <input
            type="file"
            ref={fileInputRef}
            disabled={areDocumentsDisabled}
            onChange={onFileUpload}
            className="hidden"
            data-cy="static_part_replacemet_documents_input"
          />
        </div>
      }
    >
      <div className="flex flex-wrap gap-3 pl-3 pr-3">
        {attachedDocumentsFiles.map((attachedDocumentFile) => (
          <div key={attachedDocumentFile.id}>
            <AttachedDocumentFileView
              attachedDocumentFile={attachedDocumentFile}
              isDisabled={areDocumentsDisabled}
              iconSize={2}
              className="max-w-[220px] max-h-[110px]"
              onDocumentDelete={() => onDeleteDocumentClick(attachedDocumentFile.id)}
            />
          </div>
        ))}
      </div>
    </ShutdownDetailCollapsible>
  )
}
